/**
 * ArchiveSessionDialog — 3-step safe archive flow for a session.
 *
 * Step 1: Force snapshot download (must complete before continuing).
 * Step 2: Type session name to confirm.
 * Step 3: Final irreversible "Archive" action with reason.
 *
 * Also exposes:
 *  - "Wipe demo data" — only enabled when session.is_test_data === true AND archived.
 *  - "Unarchive"      — when session is currently archived.
 *  - "Flag as test data" toggle — gates the wipe action.
 */
import React, { useState } from "react";
import {
  Archive,
  ArrowCounterClockwise,
  DownloadSimple,
  Warning,
  Trash,
  CheckCircle,
} from "@phosphor-icons/react";
import api, { API_BASE, formatApiError, tokenStore } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";

async function downloadSnapshot(schoolId, sessionId) {
  const token = tokenStore.get();
  const res = await fetch(
    `${API_BASE}/super-admin/schools/${schoolId}/sessions/${sessionId}/export`,
    { headers: { Authorization: `Bearer ${token}` } },
  );
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `session-${sessionId}.json`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export default function ArchiveSessionDialog({
  open,
  onOpenChange,
  session,
  schoolId,
  onChanged,
}) {
  const [stage, setStage] = useState(1); // 1 = backup, 2 = confirm, 3 = ready
  const [confirmName, setConfirmName] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [wipePhrase, setWipePhrase] = useState("");

  React.useEffect(() => {
    if (open) {
      setStage(session?.is_archived ? 3 : 1);
      setConfirmName("");
      setReason("");
      setWipePhrase("");
    }
  }, [open, session]);

  if (!session) return null;
  const archived = session.is_archived;
  const testData = session.is_test_data;

  const doDownload = async () => {
    setBusy(true);
    try {
      await downloadSnapshot(schoolId, session.id);
      toast.success("Snapshot downloaded");
      setStage(2);
    } catch (e) {
      toast.error(formatApiError(e));
    }
    setBusy(false);
  };

  const doArchive = async () => {
    setBusy(true);
    try {
      await api.post(
        `/super-admin/schools/${schoolId}/sessions/${session.id}/archive`,
        { confirm_session_name: confirmName, reason },
      );
      toast.success("Session archived");
      onChanged?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(formatApiError(e));
    }
    setBusy(false);
  };

  const doUnarchive = async () => {
    setBusy(true);
    try {
      await api.post(`/super-admin/schools/${schoolId}/sessions/${session.id}/unarchive`);
      toast.success("Session unarchived");
      onChanged?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(formatApiError(e));
    }
    setBusy(false);
  };

  const toggleTestData = async () => {
    setBusy(true);
    try {
      await api.patch(
        `/super-admin/schools/${schoolId}/sessions/${session.id}/flag-test-data`,
        { is_test_data: !testData },
      );
      toast.success(testData ? "Test-data flag removed" : "Flagged as test data");
      onChanged?.();
    } catch (e) {
      toast.error(formatApiError(e));
    }
    setBusy(false);
  };

  const doWipe = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(
        `/super-admin/schools/${schoolId}/sessions/${session.id}/wipe-demo-data`,
        { confirm_session_name: confirmName, confirm_phrase: wipePhrase },
      );
      toast.success(
        `Wiped ${data.deleted_payments} payments, ${data.deleted_students} students, ${data.deleted_classes} classes`,
      );
      onChanged?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(formatApiError(e));
    }
    setBusy(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl" data-testid="archive-session-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <Archive size={20} weight="duotone" /> Manage session: {session.name}
          </DialogTitle>
          <DialogDescription>
            Archive locks the session as read-only (reversible). Wipe is permanent and only allowed for test sessions.
          </DialogDescription>
        </DialogHeader>

        <div className="text-xs text-slate-500 flex items-center gap-2 flex-wrap">
          {archived && (
            <span className="px-2 py-0.5 rounded bg-amber-100 text-amber-800 font-semibold uppercase tracking-wider">
              Archived
            </span>
          )}
          {testData && (
            <span className="px-2 py-0.5 rounded bg-purple-100 text-purple-800 font-semibold uppercase tracking-wider">
              Test data
            </span>
          )}
          {session.is_active && (
            <span className="px-2 py-0.5 rounded bg-emerald-100 text-emerald-800 font-semibold uppercase tracking-wider">
              Active
            </span>
          )}
        </div>

        {!archived ? (
          <div className="space-y-4">
            {/* Step 1 — backup */}
            <Step n={1} active={stage === 1} done={stage > 1}
                  title="Download a full JSON backup">
              <p className="text-xs text-slate-500 mb-2">
                You must download the snapshot before archiving. It contains all
                students, payments, fee structures, and the audit trail.
              </p>
              <Button
                variant="outline"
                onClick={doDownload}
                disabled={busy}
                className="gap-1.5"
                data-testid="archive-step1-download"
              >
                <DownloadSimple size={14} /> Download backup JSON
              </Button>
            </Step>

            {/* Step 2 — type session name */}
            <Step n={2} active={stage === 2} done={stage > 2}
                  title="Type the session name to confirm">
              <Label className="text-xs">Session name</Label>
              <Input
                value={confirmName}
                onChange={(e) => setConfirmName(e.target.value)}
                placeholder={session.name}
                className="mt-1"
                disabled={stage < 2}
                data-testid="archive-confirm-name"
              />
              <Button
                size="sm"
                variant="outline"
                disabled={stage < 2 || confirmName !== session.name}
                onClick={() => setStage(3)}
                className="mt-2"
                data-testid="archive-step2-confirm"
              >
                Continue
              </Button>
            </Step>

            {/* Step 3 — reason + final */}
            <Step n={3} active={stage === 3} done={false}
                  title="Reason and archive">
              <Label className="text-xs">Reason (≥ 3 chars)</Label>
              <Input
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="End of academic year"
                className="mt-1"
                disabled={stage < 3}
                data-testid="archive-reason"
              />
              <Button
                onClick={doArchive}
                disabled={stage < 3 || busy || reason.trim().length < 3}
                className="bg-amber-700 hover:bg-amber-800 mt-3 gap-1.5"
                data-testid="archive-final-btn"
              >
                <Archive size={14} /> {busy ? "Archiving…" : "Archive session"}
              </Button>
            </Step>
          </div>
        ) : (
          /* Archived → unarchive + (if test) wipe */
          <div className="space-y-4">
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-md text-xs text-amber-900">
              This session is archived. New collections and edits are blocked. You can unarchive
              to restore writes, or — if it's flagged as test data — permanently wipe it.
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <Button onClick={doUnarchive} disabled={busy} variant="outline" className="gap-1.5" data-testid="archive-unarchive-btn">
                <ArrowCounterClockwise size={14} /> Unarchive
              </Button>
              <Button onClick={toggleTestData} disabled={busy} variant="outline" className="gap-1.5" data-testid="archive-toggle-test-data">
                <Warning size={14} /> {testData ? "Remove test-data flag" : "Flag as test data"}
              </Button>
            </div>

            {testData && (
              <div className="border-2 border-red-300 bg-red-50 rounded-md p-4 space-y-2">
                <div className="flex items-center gap-2 font-bold text-red-800">
                  <Trash size={16} weight="fill" /> Permanent wipe (test data only)
                </div>
                <p className="text-xs text-red-700">
                  Deletes <strong>all</strong> students, payments, classes, fee structures
                  and teacher assignments for this session. The session record itself is
                  retained as a tombstone for audit. <strong>Cannot be undone.</strong>
                </p>
                <div>
                  <Label className="text-xs">Type session name: <strong>{session.name}</strong></Label>
                  <Input
                    value={confirmName}
                    onChange={(e) => setConfirmName(e.target.value)}
                    className="mt-1"
                    data-testid="wipe-confirm-name"
                  />
                </div>
                <div>
                  <Label className="text-xs">Type exactly: <code className="font-mono bg-red-100 px-1">DELETE TEST DATA</code></Label>
                  <Input
                    value={wipePhrase}
                    onChange={(e) => setWipePhrase(e.target.value)}
                    className="mt-1"
                    data-testid="wipe-confirm-phrase"
                  />
                </div>
                <Button
                  onClick={doWipe}
                  disabled={busy || confirmName !== session.name || wipePhrase !== "DELETE TEST DATA"}
                  className="bg-red-700 hover:bg-red-800 gap-1.5"
                  data-testid="wipe-final-btn"
                >
                  <Trash size={14} weight="fill" /> {busy ? "Wiping…" : "Permanently wipe data"}
                </Button>
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Step({ n, active, done, title, children }) {
  return (
    <div className={`border rounded-md p-3 ${active ? "border-blue-300 bg-blue-50" : done ? "border-emerald-200 bg-emerald-50/50" : "border-slate-200"}`}>
      <div className="flex items-center gap-2 mb-1.5">
        <div className={`h-6 w-6 rounded-full flex items-center justify-center text-xs font-bold ${
          done ? "bg-emerald-600 text-white" : active ? "bg-blue-700 text-white" : "bg-slate-200 text-slate-500"
        }`}>
          {done ? <CheckCircle size={14} weight="fill" /> : n}
        </div>
        <div className="text-sm font-semibold text-slate-800">{title}</div>
      </div>
      {children}
    </div>
  );
}
